import { useMemo } from 'react'
import { GeoJsonFillLayer, GeoJsonLineLayer, MapMarker } from '@wehunt/map'
import { useTranslation } from '../../i18n'
import { propertyOuterCount, propertyRings, type PropertyFeature } from './propertyTapResult'
import { PARCEL_FILL_ID, PARCEL_LINE_ID } from './editorLayerIds'
import type { GeoJsonFeatureCollection, GeoJsonMultiPolygonFeature } from '../../lib/mapGeoJson'
import type { GeoPt } from '../../lib/types'
import { ringsToPolygons } from '../../lib/polygonUnion'

type GeoJsonPos = [number, number]
const toPos = (p: GeoPt): GeoJsonPos => [p.lon, p.lat]

function closedRing(ring: readonly GeoPt[]): GeoJsonPos[] {
  const coords = ring.map(toPos)
  const [fx, fy] = coords[0]
  const [lx, ly] = coords[coords.length - 1]
  return fx === lx && fy === ly ? coords : [...coords, [fx, fy]]
}

const SELECTED_FILL = 'rgba(168,30,140,0.22)'
const UNSELECTED_FILL = 'rgba(255,255,255,0.05)'
const SELECTED_STROKE = '#a81e8c'
const UNSELECTED_STROKE = 'rgba(255,255,255,0.7)'

export interface EditorParcelLayerProps {
  showParcels: boolean
  mapProperties: PropertyFeature[]
  propertiesLoading: boolean
  selectedParcels: Set<string>
  onToggleParcel: (id: string) => void
}

/** Traced-parcel outlines + selected/unselected circle markers, exactly the
 *  presentation `FullscreenMapCanvas`'s `showParcels` block draws. The outline
 *  fill layer (`PARCEL_FILL_ID`) doubles as the click router's hit target for
 *  `onToggleParcel` — its geometry already covers the whole clickable area, so
 *  a separate invisible layer would be redundant. */
export function EditorParcelLayer({
  showParcels,
  mapProperties,
  propertiesLoading,
  selectedParcels,
  onToggleParcel,
}: EditorParcelLayerProps) {
  const { t } = useTranslation('map')

  const outlineFC = useMemo<GeoJsonFeatureCollection<GeoJsonMultiPolygonFeature>>(() => {
    const features: GeoJsonMultiPolygonFeature[] = mapProperties
      .filter((p) => p.polygon && p.polygon.length >= 3)
      .map((p) => ({
        type: 'Feature',
        properties: { id: p.id, selected: selectedParcels.has(p.id) },
        geometry: {
          type: 'MultiPolygon',
          // One GeoJSON polygon per outer, each with the holes that lie inside it, so
          // a parcel around another owner's land is drawn with that land cut out.
          coordinates: ringsToPolygons(propertyRings(p), propertyOuterCount(p)).map((poly) =>
            poly.map(closedRing),
          ),
        },
      }))
    return { type: 'FeatureCollection', features }
  }, [mapProperties, selectedParcels])

  if (!showParcels) return null

  return (
    <>
      <GeoJsonFillLayer
        id={PARCEL_FILL_ID}
        data={outlineFC}
        paint={{ 'fill-color': ['case', ['get', 'selected'], SELECTED_FILL, UNSELECTED_FILL] }}
      />
      <GeoJsonLineLayer
        id={PARCEL_LINE_ID}
        data={outlineFC}
        paint={{
          'line-color': ['case', ['get', 'selected'], SELECTED_STROKE, UNSELECTED_STROKE],
          'line-width': ['case', ['get', 'selected'], 2, 1],
        }}
      />

      {mapProperties.map((p) => {
        const selected = selectedParcels.has(p.id)
        return (
          <MapMarker key={p.id} lat={p.lat} lon={p.lon} anchor="center">
            <div
              onClick={(e) => {
                e.stopPropagation()
                onToggleParcel(p.id)
              }}
              style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
              <span
                style={{
                  fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif',
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#fff',
                  textShadow: '0 0 2px rgba(0,0,0,0.7), 0 0 2px rgba(0,0,0,0.7)',
                  marginBottom: 2,
                  whiteSpace: 'nowrap',
                }}
              >
                {p.designation}
              </span>
              <svg width={20} height={20} viewBox="0 0 20 20" style={{ display: 'block' }}>
                <circle cx={10} cy={10} r={9} fill="rgba(0,0,0,0.35)" />
                <circle
                  cx={10}
                  cy={10}
                  r={7}
                  fill={selected ? SELECTED_STROKE : 'rgba(255,255,255,0.85)'}
                  stroke="#fff"
                  strokeWidth={1.5}
                />
              </svg>
            </div>
          </MapMarker>
        )
      })}

      {propertiesLoading && (
        <div
          data-testid="editor-properties-loading"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            fontSize: 13,
            fontWeight: 600,
            color: 'rgba(255,255,255,0.85)',
            textShadow: '0 0 3px rgba(0,0,0,0.8), 0 0 3px rgba(0,0,0,0.8)',
          }}
        >
          {t('canvas.tracingProperty')}
        </div>
      )}
    </>
  )
}
